import { useQuery } from "@tanstack/react-query";
import { getAppSetting } from "@/lib/app-settings";
import type { CartItem } from "@/lib/cart";

/**
 * Single source of truth for monetary rounding across cart, drawer, and checkout.
 * Rule: round to 2dp using half-away-from-zero (`Math.round(n * 100) / 100`).
 */
export function roundMoney(n: number): number {
  if (!Number.isFinite(n)) return 0;
  // Add a tiny epsilon to avoid 1.005 → 1.00 floating-point surprises.
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * Tax rounding rules (applied consistently everywhere):
 *  1. Each line's net subtotal is rounded to 2dp.
 *  2. Each line's tax = round(lineNet * effectiveRate) — rounded per line.
 *  3. Grand subtotal/tax = sum of the rounded line values.
 *  4. Shipping is rounded once, then added to the grand total.
 * This guarantees the per-row displays add up exactly to the summary totals.
 */

export interface TaxConfig {
  enabled: boolean;
  globalRate: number; // percentage, e.g. 20 for 20%
  label: string;
}

export const DEFAULT_TAX_CONFIG: TaxConfig = {
  enabled: false,
  globalRate: 0,
  label: "VAT",
};

export function useTaxConfig() {
  return useQuery({
    queryKey: ["app-settings", "tax"],
    queryFn: async (): Promise<TaxConfig> => {
      const [enabled, rate, label] = await Promise.all([
        getAppSetting("vat_enabled"),
        getAppSetting("vat_rate"),
        getAppSetting("vat_label"),
      ]);
      const parsedRate = parseFloat(rate ?? "0");
      return {
        enabled: enabled === "true",
        globalRate: Number.isFinite(parsedRate) ? parsedRate : 0,
        label: label || "VAT",
      };
    },
    staleTime: 60_000,
    initialData: DEFAULT_TAX_CONFIG,
  });
}

/** Returns the effective tax rate (percentage) for a single line. */
export function effectiveTaxRate(item: CartItem, cfg: TaxConfig): number {
  if (!cfg.enabled) return 0;
  // tax_use_global defaults to true when missing.
  const useGlobal = item.taxUseGlobal !== false;
  if (useGlobal) return cfg.globalRate;
  const override = item.taxRateOverride;
  return typeof override === "number" && Number.isFinite(override) ? override : 0;
}

export function lineNet(item: CartItem): number {
  const price = parseFloat(item.pricePerMetre || "0") || 0;
  return roundMoney(price * (item.metres || 0) * (item.quantity || 1));
}

export function lineTax(item: CartItem, cfg: TaxConfig): number {
  const rate = effectiveTaxRate(item, cfg);
  if (!rate) return 0;
  return roundMoney(lineNet(item) * (rate / 100));
}

export interface CartTotals {
  subtotal: number;
  tax: number;
  shipping: number;
  total: number;
  taxLabel: string;
  taxEnabled: boolean;
  /** Display suffix like " (20%)" when all taxed lines share a single rate; "" otherwise. */
  taxRateLabel: string;
  lines: Array<{ id: string; net: number; tax: number; total: number; rate: number }>;
}

export function computeCartTotals(
  items: CartItem[],
  cfg: TaxConfig,
  shippingPrice: number = 0,
): CartTotals {
  const lines = items.map((i) => {
    const net = lineNet(i);
    const tax = lineTax(i, cfg);
    return {
      id: i.id,
      net,
      tax,
      total: roundMoney(net + tax),
      rate: effectiveTaxRate(i, cfg),
    };
  });
  const subtotal = roundMoney(lines.reduce((s, l) => s + l.net, 0));
  const tax = roundMoney(lines.reduce((s, l) => s + l.tax, 0));
  const shipping = roundMoney(shippingPrice || 0);
  const total = roundMoney(subtotal + tax + shipping);
  const taxedRates = Array.from(new Set(lines.filter((l) => l.tax > 0).map((l) => l.rate)));
  const taxRateLabel = cfg.enabled && taxedRates.length === 1 ? ` (${taxedRates[0]}%)` : "";
  return {
    subtotal,
    tax,
    shipping,
    total,
    taxLabel: cfg.label,
    taxEnabled: cfg.enabled,
    taxRateLabel,
    lines,
  };
}

/** Hook combining tax config + cart items into a fully resolved totals breakdown. */
export function useCartTotals(items: CartItem[], shippingPrice: number = 0): CartTotals {
  const { data: cfg = DEFAULT_TAX_CONFIG } = useTaxConfig();
  return computeCartTotals(items, cfg, shippingPrice);
}
